import { clampParcelPosition, parcelDistance, parcelPositionAt, type ParcelPosition } from "../config/parcelSpatial";

export type ParcelReminderPoi = "home" | "company";
export type ParcelReminderResult = "pending" | "empty" | "transit" | "error" | "timeout";
export type ParcelReminderSurface = "hidden" | "card" | "capsule";
export type ParcelReminderPhase = "outside" | "querying" | "reminding" | "ignored" | "empty" | "error" | "parked" | "disabled";
export type ParcelReminderQueryReason = "entry" | "park" | "manual";

export interface ParcelReminderState {
  distanceM: number;
  vehiclePosition: ParcelPosition;
  gear: "D" | "P";
  poi: ParcelReminderPoi;
  result: ParcelReminderResult;
  surface: ParcelReminderSurface;
  phase: ParcelReminderPhase;
  pendingCount: number;
  inTransitCount: number;
  dailyTriggered: boolean;
  /** First-P consumption is isolated per POI; tripParkChecked mirrors the current POI. */
  tripParkChecked: boolean;
  parkCheckedByPoi: Record<ParcelReminderPoi, boolean>;
  enabled: boolean;
  authorized: boolean;
  poiUploadAuthorized: boolean;
  poiConfigured: boolean;
  message: string;
  queryCount: number;
  queryReason: ParcelReminderQueryReason | null;
  ttsText: string;
  events: string[];
  /** The timer must capture this token when it dispatches QUERY_COMPLETE. */
  queryToken: number;
  queryPoi: ParcelReminderPoi | null;
  queryResult: ParcelReminderResult;
  activeReminder: boolean;
  mode: "proactive" | "manual" | null;
  triggeredByPoi: Record<ParcelReminderPoi, boolean>;
  ttsSequence: number;
}

export type ParcelReminderEvent =
  | { type: "MOVE"; distanceM: number }
  | { type: "POSITION"; position: ParcelPosition }
  | { type: "GEAR"; gear: "D" | "P" }
  | { type: "RESULT"; result: ParcelReminderResult }
  | { type: "POI"; poi: ParcelReminderPoi }
  | { type: "ENABLE"; enabled: boolean }
  | { type: "AUTHORIZE"; authorized: boolean }
  | { type: "POI_AUTHORIZE"; authorized: boolean }
  | { type: "CONFIGURE_POI"; configured: boolean }
  | { type: "QUERY_COMPLETE"; queryToken: number }
  | { type: "ASK" | "NEW_TRIP" | "NEW_DAY" | "COLLAPSE" | "OPEN" | "IGNORE" };

const FENCE_RADIUS_M = 2000;
const PARK_REMINDER_RADIUS_M = 500;
const MAX_DISTANCE_M = 2600;

function poiName(poi: ParcelReminderPoi) {
  return poi === "home" ? "家" : "公司";
}

function record(state: ParcelReminderState, message: string): ParcelReminderState {
  return { ...state, message, events: [...state.events, message].slice(-12) };
}

function speak(state: ParcelReminderState, text: string): ParcelReminderState {
  return { ...state, ttsText: text, ttsSequence: state.ttsSequence + 1 };
}

function ready(state: ParcelReminderState) {
  return state.enabled && state.authorized && state.poiUploadAuthorized && state.poiConfigured;
}

function prerequisiteMessage(state: ParcelReminderState) {
  if (!state.authorized) return "请先完成支付宝安装、登录与授权。";
  if (!state.poiUploadAuthorized) return "请先授权将家／公司位置上传给支付宝。";
  if (!state.poiConfigured) return "请先设置家或公司位置。";
  return "主动提醒已关闭。";
}

function end(state: ParcelReminderState, phase: ParcelReminderPhase, message: string): ParcelReminderState {
  return record({
    ...state,
    phase,
    surface: "hidden",
    activeReminder: false,
    queryToken: state.queryToken + 1,
    ttsText: ""
  }, message);
}

function beginQuery(state: ParcelReminderState, reason: ParcelReminderQueryReason): ParcelReminderState {
  const triggeredByPoi = reason === "entry"
    ? { ...state.triggeredByPoi, [state.poi]: true }
    : state.triggeredByPoi;
  return record({
    ...state,
    phase: "querying",
    surface: "hidden",
    activeReminder: true,
    mode: reason === "manual" ? "manual" : "proactive",
    queryCount: state.queryCount + 1,
    queryToken: state.queryToken + 1,
    queryReason: reason,
    queryPoi: reason === "manual" ? null : state.poi,
    queryResult: state.result,
    triggeredByPoi,
    dailyTriggered: triggeredByPoi[state.poi],
    ttsText: ""
  }, reason === "manual"
    ? "正在查询用户名下的快递，不受地理围栏与主动提醒开关限制。"
    : `${reason === "park" ? `本行程在${poiName(state.poi)}附近首次挂入 P，重新` : "今日首次进入 2 km 围栏，"}查询${poiName(state.poi)}位置 500 m 内的待取快递。`);
}

/** Starts at the already-completed first query so the presentation opens on its key card. */
export function createParcelReminderState(): ParcelReminderState {
  const message = "今日首次进入家周边 2 km，已查询家位置 500 m 内的快递。";
  return {
    distanceM: 1800,
    vehiclePosition: parcelPositionAt("home", 1800),
    gear: "D",
    poi: "home",
    result: "pending",
    surface: "card",
    phase: "reminding",
    pendingCount: 2,
    inTransitCount: 0,
    dailyTriggered: true,
    tripParkChecked: false,
    parkCheckedByPoi: { home: false, company: false },
    enabled: true,
    authorized: true,
    poiUploadAuthorized: true,
    poiConfigured: true,
    message,
    queryCount: 1,
    queryReason: "entry",
    ttsText: "家附近有 2 件待取快递。",
    events: [message],
    queryToken: 1,
    queryPoi: "home",
    queryResult: "pending",
    activeReminder: true,
    mode: "proactive",
    triggeredByPoi: { home: true, company: false },
    ttsSequence: 1
  };
}

export function parcelReminderReducer(state: ParcelReminderState, event: ParcelReminderEvent): ParcelReminderState {
  switch (event.type) {
    case "POSITION": {
      if (!Number.isFinite(event.position.x) || !Number.isFinite(event.position.y)) return state;
      const vehiclePosition = clampParcelPosition(event.position);
      if (vehiclePosition.x === state.vehiclePosition.x && vehiclePosition.y === state.vehiclePosition.y) return state;
      const poi = parcelDistance(vehiclePosition, "home") <= FENCE_RADIUS_M ? "home"
        : parcelDistance(vehiclePosition, "company") <= FENCE_RADIUS_M ? "company" : state.poi;
      const distanceM = parcelDistance(vehiclePosition, poi);
      const previousDistance = parcelDistance(state.vehiclePosition, poi);
      let next: ParcelReminderState = { ...state, vehiclePosition, distanceM, poi,
        gear: "D", tripParkChecked: state.parkCheckedByPoi[poi], dailyTriggered: state.triggeredByPoi[poi] };
      // User-initiated account queries remain independent of the car's location.
      if (state.mode === "manual" && state.activeReminder) return next;
      if (state.phase === "querying" && state.queryReason === "park") {
        next = end(next, "outside", "车辆已继续行驶，本次停车复查结束。下次停车检查需开始新行程。");
      }
      if (poi !== state.poi) next = end({ ...next, mode: null, queryPoi: null, queryReason: null }, "outside", `已驶入${poiName(poi)}附近。`);
      if (distanceM > FENCE_RADIUS_M) {
        return next.activeReminder ? end(next, "outside", "已离开本次对应位置的 2 km 围栏，本次提醒结束。") : next;
      }
      if (previousDistance > FENCE_RADIUS_M && ready(next) && !next.triggeredByPoi[poi]) return beginQuery(next, "entry");
      return next;
    }
    case "MOVE": {
      if (!Number.isFinite(event.distanceM)) return state;
      const distanceM = Math.max(0, Math.min(MAX_DISTANCE_M, event.distanceM));
      const next = { ...state, distanceM, vehiclePosition: parcelPositionAt(state.poi, distanceM) };
      if (state.mode === "manual" && state.activeReminder) return next;
      if (distanceM > FENCE_RADIUS_M) {
        if (state.activeReminder) return end(next, "outside", "已离开本次对应位置的 2 km 围栏，本次提醒结束。");
        return next;
      }
      if (state.distanceM > FENCE_RADIUS_M && ready(next) && !next.triggeredByPoi[next.poi]) {
        return beginQuery(next, "entry");
      }
      return next;
    }
    case "GEAR": {
      if (event.gear === state.gear) return state;
      const next = { ...state, gear: event.gear };
      if (event.gear !== "P" || state.parkCheckedByPoi[state.poi]) return next;
      const checked = { ...next, tripParkChecked: true, parkCheckedByPoi: { ...state.parkCheckedByPoi, [state.poi]: true } };
      if (state.mode === "proactive" && state.activeReminder && ready(state)) {
        if (state.distanceM <= PARK_REMINDER_RADIUS_M) return beginQuery(checked, "park");
        return end(checked, "parked", "停车位置超过500 m，本次提醒静默结束。");
      }
      return record(checked, state.phase === "ignored"
        ? "本行程首次挂入 P；本次提醒已忽略，不再二次查询。"
        : "已记录本行程首次挂入 P；当前没有需要再次提醒的快递任务。");
    }
    case "RESULT": {
      const labels: Record<ParcelReminderResult, string> = {
        pending: "有待取快递", empty: "无待取快递", transit: "有运输中快递", error: "查询失败", timeout: "查询超时"
      };
      return record({ ...state, result: event.result }, `下次查询结果已设为${labels[event.result]}。`);
    }
    case "POI": {
      if (event.poi === state.poi) return state;
      const next = end({
        ...state,
        poi: event.poi,
        gear: "D",
        tripParkChecked: state.parkCheckedByPoi[event.poi],
        distanceM: 1800,
        vehiclePosition: parcelPositionAt(event.poi, 1800),
        dailyTriggered: state.triggeredByPoi[event.poi],
        mode: null,
        queryPoi: null,
        queryReason: null
      }, ready(state) ? "outside" : "disabled", `已切换到${poiName(event.poi)}位置。`);
      if (ready(next) && !next.dailyTriggered) return beginQuery(next, "entry");
      return record(next, next.dailyTriggered
        ? `今日已触发过${poiName(next.poi)}周边提醒，不重复触发。`
        : prerequisiteMessage(next));
    }
    case "ENABLE": {
      if (!event.enabled) {
        const next = { ...state, enabled: false };
        if (state.mode === "manual" && state.activeReminder) {
          return speak(record(next, "主动提醒已关闭，本次主动询问继续。"), "好的，快递主动提醒已关闭，本次快递查询会继续。");
        }
        return speak(end(next, "disabled", "主动提醒已关闭。"), "好的，快递主动提醒已关闭。");
      }
      if (!state.authorized || !state.poiUploadAuthorized || !state.poiConfigured) {
        const prerequisite = prerequisiteMessage(state);
        const spokenPrerequisite = prerequisite.replace("家／公司", "家或公司");
        if (state.mode === "manual" && state.activeReminder && state.authorized) {
          return speak(record({ ...state, enabled: false }, `${prerequisite}本次主动询问继续。`), spokenPrerequisite);
        }
        const next = end({ ...state, enabled: false }, "disabled", prerequisite);
        return speak(next, spokenPrerequisite);
      }
      if (state.enabled) return state;
      return speak(record({ ...state, enabled: true, phase: state.activeReminder ? state.phase : "outside" }, "主动提醒已开启，等待今日首次进入家或公司周边 2 km。"), "好的，快递主动提醒已开启。");
    }
    case "AUTHORIZE": {
      if (event.authorized) return record({ ...state, authorized: true }, "支付宝安装、登录与授权已完成。");
      return end({ ...state, authorized: false, enabled: false }, "disabled", "支付宝授权不可用，主动提醒已关闭；请完成安装、登录与授权。");
    }
    case "POI_AUTHORIZE": {
      if (event.authorized) return record({ ...state, poiUploadAuthorized: true }, "已授权将家／公司位置上传给支付宝。");
      const next = { ...state, poiUploadAuthorized: false, enabled: false };
      if (state.mode === "manual" && state.activeReminder) return record(next, "位置上传未授权；本次用户名下快递查询继续。");
      return end(next, "disabled", "位置上传未授权，主动提醒已关闭。");
    }
    case "CONFIGURE_POI": {
      if (event.configured) return record({ ...state, poiConfigured: true }, "家／公司位置已设置。");
      const next = { ...state, poiConfigured: false, enabled: false };
      if (state.mode === "manual" && state.activeReminder) return record(next, "家／公司位置未设置；本次主动询问仍可继续。");
      return end(next, "disabled", "请先设置家或公司位置。");
    }
    case "ASK": {
      if (!state.authorized) {
        const next = end(state, "disabled", "主动询问前，请先完成支付宝安装、登录与授权。");
        return speak(next, "请先完成支付宝安装、登录与授权，再查询快递。");
      }
      return beginQuery(state, "manual");
    }
    case "NEW_TRIP":
    case "NEW_DAY": {
      const triggeredByPoi = event.type === "NEW_DAY" ? { home: false, company: false } : state.triggeredByPoi;
      return end({
        ...state,
        distanceM: MAX_DISTANCE_M,
        vehiclePosition: parcelPositionAt(state.poi, MAX_DISTANCE_M),
        gear: "D",
        tripParkChecked: false,
        parkCheckedByPoi: { home: false, company: false },
        triggeredByPoi,
        dailyTriggered: triggeredByPoi[state.poi],
        mode: null,
        queryReason: null,
        queryPoi: null
      }, ready(state) ? "outside" : "disabled", event.type === "NEW_DAY"
        ? "已开始新的一天，家和公司的当日首次触发记录已重置。"
        : "新行程已开始；首次挂 P 已重置，当日围栏触发记录保留。");
    }
    case "QUERY_COMPLETE": {
      if (state.phase !== "querying" || !state.activeReminder || event.queryToken !== state.queryToken) return state;
      const manual = state.queryReason === "manual";
      if (!state.authorized || (!manual && (!ready(state) || state.distanceM > FENCE_RADIUS_M || state.queryPoi !== state.poi))) return state;
      if (state.queryReason === "park" && (state.gear !== "P" || state.distanceM > PARK_REMINDER_RADIUS_M)) {
        return end(state, "parked", "停车提醒条件已不满足，本次提醒静默结束。");
      }
      const result = state.queryResult;
      if (result === "error" || result === "timeout") {
        return speak(end(state, "error", result === "timeout" ? "快递查询超时，本次查询结束。" : "快递查询失败，本次查询结束。"), result === "timeout" ? "快递查询超时，请稍后再试。" : "暂时无法查询快递，请稍后再试。");
      }
      const pendingCount = result === "pending" ? 2 : 0;
      const inTransitCount = result === "transit" ? 1 : 0;
      const next = { ...state, pendingCount, inTransitCount };
      if (state.queryReason === "park") {
        const finished = end(next, "parked", result === "pending"
          ? "停车后二次查询仍有待取快递；已播报提醒并关闭胶囊。"
          : "停车后二次查询已无待取快递，胶囊已关闭。");
        return result === "pending"
          ? speak(finished, "别忘了取快递。")
          : finished;
      }
      if (manual) {
        return speak(record({ ...next, phase: result === "empty" ? "empty" : "reminding", surface: "card" }, "主动询问结果已展示，15 秒后结束，不保留胶囊。"), result === "empty"
          ? "你目前没有待取或运输中的快递。"
          : result === "transit" ? "你有 1 件快递正在运输中，暂时没有待取快递。" : `你有 ${pendingCount} 件待取快递。`);
      }
      if (result !== "pending") return end(next, "empty", `已查询${poiName(state.poi)}位置 500 m 内的快递，暂无待取，本次提醒结束。`);
      return speak(record({ ...next, phase: "reminding", surface: "card" }, "已展示待取提醒；15 秒后或点击卡片外部收为胶囊。"), `${poiName(state.poi)}附近有 ${pendingCount} 件待取快递。`);
    }
    case "COLLAPSE": {
      if (!state.activeReminder || state.surface !== "card") return state;
      if (state.mode === "manual") return end(state, state.phase, "本次主动询问已结束，不保留胶囊。");
      return record({ ...state, surface: "capsule" }, "提醒已收为胶囊，点击胶囊可重新展开；首次挂入 P 时再次检查。");
    }
    case "OPEN":
      return state.activeReminder && state.surface === "capsule"
        ? record({ ...state, surface: "card" }, "已重新展开快递提醒。")
        : state;
    case "IGNORE":
      return speak(end(state, "ignored", "本次提醒已忽略，不再进行停车二次提醒；包裹状态不因此改变。"), "好的，本次不再提醒。");
    default:
      return state;
  }
}
